import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { getAuth, applyActionCode } from 'firebase/auth';

const EmailVerification = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const mode = searchParams.get('mode');
        const oobCode = searchParams.get('oobCode');
        
        if (!mode || !oobCode) {
          setStatus('error');
          setMessage('Invalid verification link. Please check your email and try again.');
          return;
        }

        if (mode === 'verifyEmail') {
          const auth = getAuth();
          await applyActionCode(auth, oobCode);
          
          setStatus('success');
          setMessage('Email verified successfully! You can now log in to your account.');
          toast.success('Email verified successfully!');
          
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else if (mode === 'resetPassword') {
          navigate(`/reset-password?oobCode=${oobCode}`);
        } else {
          setStatus('error');
          setMessage('Unsupported verification mode.');
        }
      } catch (error) {
        console.error('Email verification error:', error);
        setStatus('error');
        
        let errorMessage = 'Failed to verify email. ';
        if (error.code === 'auth/expired-action-code') {
          errorMessage += 'The verification link has expired. Please request a new one.';
        } else if (error.code === 'auth/invalid-action-code') {
          errorMessage += 'The verification link is invalid or has already been used.';
        } else {
          errorMessage += error.message || 'Please try again or contact support.';
        }
        
        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl text-center">
        {status === 'verifying' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Email</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">✅ Email Verified!</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-800">Redirecting to login page in 3 seconds...</p>
            </div>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">❌ Verification Failed</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <button 
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition"
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerification;